// Import Dependencies
import { Navigate } from 'react-router';

// Local Imports
import { AppLayout } from 'app/layouts/AppLayout';
import { DynamicLayout } from 'app/layouts/DynamicLayout';
import AuthGuard from 'middleware/AuthGuard';

// ----------------------------------------------------------------------

const protectedRoutes = {
  id: 'protected',
  Component: AuthGuard,
  children: [
    // The dynamic layout supports both the main layout and the sideblock.
    {
      Component: DynamicLayout,
      children: [
        {
          index: true,
          element: <Navigate to={'dashboard'} />,
        },
        {
          path: 'dashboard',
          lazy: async () => ({
            Component: (await import('app/pages/industrial/dashboardPage'))
              .default,
          }),
        },
        {
          path: 'reports',
          children: [
            {
              index: true,
              element: <Navigate to="/reports/analytics" />,
            },
            {
              path: 'analytics',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/reports/DataAnalytics')
                ).default,
              }),
            },
            {
              path: 'daily-profile',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/DailyProfile/DailyProfiles'
                  )
                ).default,
              }),
            },
            {
              path: 'weather',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/WeatherAnalysis/WeatherAnalysis'
                  )
                ).default,
              }),
            },
            {
              path: 'annual',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/reports/AnnualQI/AnnualAqi')
                ).default,
              }),
            },
            {
              path: 'seasonal-analysis',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/SeasonalAnalysis/SeasonalAnalysisPage'
                  )
                ).default,
              }),
            },
            {
              path: 'correlation-analysis',
              lazy: async () => ({
                Component: (
                  await import(
                    'app/pages/shared/reports/CorrelationAnalysis/CorrelationAnalysisPage'
                  )
                ).default,
              }),
            },
          ],
        },
        {
          path: 'alerts',
          lazy: async () => ({
            Component: (await import('app/pages/industrial/alertsPage'))
              .default,
          }),
        },
        {
          path: 'devices',

          children: [
            {
              index: true,
              element: <Navigate to="/devices/information" />,
            },
            {
              path: 'information',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/devices/Informations')
                ).default,
              }),
            },
            {
              path: 'calibration',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/devices/CalibrationOverview')
                ).default,
              }),
            },
          ],
        },
        {
          path: 'station/:stationId/calibrations/:calibrationId',
          lazy: async () => ({
            Component: (
              await import('app/pages/shared/devices/CalibrationDetails')
            ).default,
          }),
        },
        {
          path: 'devices/calibration/add',
          lazy: async () => ({
            Component: (await import('app/pages/shared/devices/Calibration'))
              .default,
          }),
        },
        {
          path: 'device-details/:stationId',
          lazy: async () => ({
            Component: (
              await import('app/pages/shared/devices/DeviceManagementDetails')
            ).default,
          }),
        },
        {
          path: 'alert-details/:alertId',
          lazy: async () => ({
            Component: (await import('app/pages/shared/alerts/AlertDetail'))
              .default,
          }),
        },
        {
          path: 'threshold-details/:thresholdId',
          lazy: async () => ({
            Component: (await import('app/pages/shared/alerts/ThresholdDetail'))
              .default,
          }),
        },
      ],
    },
    // The app layout supports only the main layout. Avoid using it for other layouts.
    {
      Component: AppLayout,
      children: [
        {
          path: 'settings',
          lazy: async () => ({
            Component: (await import('app/pages/shared/settings/Layout'))
              .default,
          }),
          children: [
            {
              index: true,
              element: <Navigate to="/settings/general" />,
            },
            {
              path: 'general',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/settings/sections/General')
                ).default,
              }),
            },
            {
              path: 'appearance',
              lazy: async () => ({
                Component: (
                  await import('app/pages/shared/settings/sections/Appearance')
                ).default,
              }),
            },
          ],
        },
      ],
    },
  ],
};

export { protectedRoutes };
